import { createSelector } from "reselect";

import type { SelectedSeatsQueryParam } from "@app/modules/results/hooks/use-navigation";
import {
  getCart,
  getInventoryReady,
  getSavedTravellers
} from "@app/modules/results/selectors/state-accessors";
import { getTripById } from "@app/modules/results/selectors/trips/get-trip";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import { isCartValid } from "./is-cart-valid";

export const ASYNC_CART_VIEWS = {
  ERROR: "error",
  LOADING_TRIP: "loading_trip",
  LOADING_SAVED_TRAVELLERS: "loading_saved_travellers",
  PARTIAL_FORM: "partial_form",
  FULL_FORM_TRIP_SOURCE: "full_form_trip_source",
  FULL_FORM_CART_SOURCE: "full_form_cart_source"
} as const;

export type AsyncCartView =
  (typeof ASYNC_CART_VIEWS)[keyof typeof ASYNC_CART_VIEWS];

export const getAsyncCartView = createSelector(
  [
    getCart,
    isCartValid,
    getInventoryReady,
    getTripById,
    getSavedTravellers,
    (
      _state: ResultsReduxState,
      _trip_id: string,
      _seats: SelectedSeatsQueryParam
    ) => null
  ],
  (cart, is_cart_valid, is_inventory_ready, trip, saved_travellers) => {
    const is_trip_found = trip !== undefined;
    const is_saved_travellers_fetched = !(saved_travellers.value === null);
    const contains_valid_cart = cart && is_cart_valid;

    if (contains_valid_cart) {
      if (cart.status === "NEW_PRICE_ACCEPTED") {
        return ASYNC_CART_VIEWS.FULL_FORM_CART_SOURCE;
      }

      if (cart.status === "REQUIRE_PRICE_CONFIRMATION") {
        return ASYNC_CART_VIEWS.FULL_FORM_TRIP_SOURCE;
      }

      return ASYNC_CART_VIEWS.FULL_FORM_CART_SOURCE;
    }

    if (!is_inventory_ready) {
      return ASYNC_CART_VIEWS.LOADING_TRIP;
    }

    if (!is_trip_found) {
      return ASYNC_CART_VIEWS.ERROR;
    }

    if (!is_saved_travellers_fetched) {
      return ASYNC_CART_VIEWS.LOADING_SAVED_TRAVELLERS;
    }

    return ASYNC_CART_VIEWS.PARTIAL_FORM;
  }
);
